import { useEffect, useReducer, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Button,
  Spinner,
} from "reactstrap";
import { getCheckPanel, updateCheckPanel } from "../../services/checkerPanel.service";
import { getEmployees } from "../../services/employees.service";
import { toast } from "react-hot-toast";
import FormInput from "../../components/FormInput";
import FormSelect from "../../components/FormSelect";
import { Formik, getIn } from "formik";
import * as Yup from "yup";
import EmptyStateVector from "../../assets/images/empty-state.svg";
import { formatIndianAmount, formatIndianInteger } from "../../utils/numberFormat";

const initialState = {
  results: [],
  page: 1,
  limit: 100,
  totalPages: 1,
  totalResults: 0,
  startIndex: 1,
  endIndex: 100,
};

function reducer(state: any, action: any) {
  switch (action.type) {
    case "SET_DATA":
      return action.payload;
    default:
      return state;
  }
}

function CheckerPanel() {
  const [data, dispatch] = useReducer(reducer, initialState);
  const [loading, setLoading] = useState(true);
  const [holdModal, setHoldModal] = useState(false);
  const [stopModal, setStopModal] = useState(false);
  const [pickList, setPickList] = useState<any>(null);
  const [pendingSummary, setPendingSummary] = useState({
    vorCount: 0,
    vorValue: 0,
    stdCount: 0,
    stdValue: 0,
  });

  const toggleHold = () => setHoldModal(!holdModal);

  const toggleStop = () => setStopModal(!stopModal);

  const setData = (data: any) => {
    dispatch({ type: "SET_DATA", payload: data });
  };

  const getPendingSummaryFromResults = (results: any[]) => {
    const vorResults = results.filter((item: any) => item?.VOR);
    const stdResults = results.filter((item: any) => !item?.VOR);

    const getTotalValue = (items: any[]) =>
      items.reduce((sum: number, item: any) => {
        return sum + Number(item?.orderAmount || 0);
      }, 0);

    return {
      vorCount: vorResults.length,
      vorValue: getTotalValue(vorResults),
      stdCount: stdResults.length,
      stdValue: getTotalValue(stdResults),
    };
  };

  useEffect(() => {
    setLoading(true);
    getCheckPanel({ page: 1, limit: 100000 })
      .then((response: any) => {
        setData(response.data);
        const results = response.data?.results || [];
        setPendingSummary(getPendingSummaryFromResults(results));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const formattedVorValue = formatIndianAmount(pendingSummary.vorValue) || "0.00";
  const formattedStdValue = formatIndianAmount(pendingSummary.stdValue) || "0.00";

  const updateResults = (id: any, updatedObject: any, response: any) => {
    let updatedResults = data.results;
    if (updatedObject.status === "STOPPED") {
      updatedResults = updatedResults.filter((pickList: any) => pickList.id !== id);
    } else {
      updatedResults = updatedResults.map((pickList: any) => {
        if (pickList.id === id) {
          return { ...pickList, ...response.data };
        }
        return pickList;
      });
    }
    setData({ ...data, results: updatedResults });
    setPendingSummary(getPendingSummaryFromResults(updatedResults));
  };

  const handleClick = (id: any, updatedObject: any) => {
    setLoading(true);
    return updateCheckPanel(id, updatedObject)
      .then((response: any) => {
        updateResults(id, updatedObject, response);
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleHoldSubmit = (id: any, updatedObject: any) => {
    handleClick(id, updatedObject).then(() => {
      toggleHold();
    });
  };

  const handleStopSubmit = (id: any, updatedObject: any) => {
    handleClick(id, updatedObject).then(() => {
      toggleStop();
    });
  };

  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      <Row className='align-items-center mb-2 page-title-row'>
        <Col sm>
          <h1 className='my-1 fw-bold h4'>Checker Panel</h1>
        </Col>
      </Row>
      <Row className='g-2 mb-2'>
        <Col md={6}>
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              background: "#f9fafb",
              padding: "0.65rem 0.8rem",
              height: "100%",
            }}
          >
            <div className='d-flex justify-content-between align-items-center mb-1'>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#4b5563" }}>STD</span>
              <span
                style={{
                  fontSize: "0.68rem",
                  background: "#e5e7eb",
                  color: "#374151",
                  fontWeight: 600,
                  padding: "1px 7px",
                  borderRadius: "999px",
                }}
              >
                {formatIndianInteger(pendingSummary.stdCount) || "0"}
              </span>
            </div>
            <h6 className='mb-0 fw-semibold' style={{ color: "#111827", fontSize: "0.98rem" }}>
              {formattedStdValue}
            </h6>
          </div>
        </Col>

        <Col md={6}>
          <div
            style={{
              border: "1px solid #bbf7d0",
              borderRadius: "8px",
              background: "#f0fdf4",
              padding: "0.65rem 0.8rem",
              height: "100%",
            }}
          >
            <div className='d-flex justify-content-between align-items-center mb-1'>
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#15803d" }}>VOR</span>
              <span
                style={{
                  fontSize: "0.68rem",
                  background: "#dcfce7",
                  color: "#166534",
                  fontWeight: 600,
                  padding: "1px 7px",
                  borderRadius: "999px",
                }}
              >
                {formatIndianInteger(pendingSummary.vorCount) || "0"}
              </span>
            </div>
            <h6 className='mb-0 fw-semibold' style={{ color: "#15803d", fontSize: "0.98rem" }}>
              {formattedVorValue}
            </h6>
          </div>
        </Col>
      </Row>

      <div className='flex-grow-1 overflow-hidden'>
        <Card className='h-100 p-3 w-100 d-flex flex-column overflow-hidden'>
          <div className='flex-grow-1 overflow-auto overflow-x-hidden'>
            {loading ? (
              <div className='align-items-center d-flex h-100 justify-content-center py-5'>
                <div className='data-grid-loading-state'>
                  <Spinner color='primary' />
                  <p className='data-grid-loading-state-title'>Loading records...</p>
                </div>
              </div>
            ) : data.results.length === 0 ? (
              <div className='align-items-center d-flex h-100 justify-content-center py-5'>
                <div className='data-grid-empty-state'>
                  <img
                    src={EmptyStateVector}
                    alt='No checker panel data'
                    className='data-grid-empty-state-image'
                  />
                  <p className='data-grid-empty-state-title'>No checker panel records found</p>
                  <p className='data-grid-empty-state-subtitle' style={{ color: "#4b5563" }}>
                    There are no checker panel records available right now.
                  </p>
                </div>
              </div>
            ) : (
              <Row className='g-3'>
                {data.results.map((pickList: any) => (
                  <Col className='mb-0' md={3} key={pickList.id}>
                    <Card
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        overflow: "hidden",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                      }}
                    >
                      <CardBody className='p-3'>
                        <div className='mb-3'>
                          <h6
                            className='mb-1 fw-semibold'
                            style={{ color: pickList.VOR ? "#16a34a" : "#111827" }}
                          >
                            {pickList.orderNumber}
                          </h6>
                          <p className='mb-0 small fw-medium' style={{ color: "#4b5563" }}>
                            {pickList.party?.name || "N/A"}
                          </p>
                        </div>

                        <div className='d-flex gap-2'>
                          {(pickList.status === "PENDING" || pickList.status === "HOLD") && (
                            <button
                              className='btn btn-sm btn-primary flex-grow-1'
                              onClick={() => {
                                handleClick(pickList.id, { status: "STARTED" });
                              }}
                            >
                              Start
                            </button>
                          )}
                          {pickList.status === "STARTED" && (
                            <>
                              <button
                                className='btn btn-sm btn-warning flex-grow-1'
                                onClick={() => {
                                  toggleHold();
                                  setPickList(pickList);
                                }}
                              >
                                Hold
                              </button>
                              <button
                                className='btn btn-sm btn-danger flex-grow-1'
                                onClick={() => {
                                  toggleStop();
                                  setPickList(pickList);
                                }}
                              >
                                Stop
                              </button>
                            </>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </Card>
      </div>
      {/* Hold Modal */}
      <Modal isOpen={holdModal} toggle={toggleHold}>
        <ModalHeader toggle={toggleHold}>Fill the details</ModalHeader>

        <Formik
          initialValues={{
            comment: "",
          }}
          validationSchema={Yup.object().shape({
            comment: Yup.string().required("Comment is required."),
          })}
          onSubmit={(values) => {
            handleHoldSubmit(pickList.id, {
              status: "HOLD",
              comment: values.comment,
            });
          }}
        >
          {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
            <>
              <ModalBody>
                <FormInput
                  label='Comment'
                  name='comment'
                  type='textarea'
                  rows='8'
                  cols='50'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={getIn(values, "comment")}
                  error={getIn(errors, "comment")}
                  touched={getIn(touched, "comment")}
                />
              </ModalBody>
              <ModalFooter>
                <Button
                  size='sm'
                  color='primary'
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  Submit
                </Button>{" "}
                <Button size='sm' color='secondary' onClick={toggleHold}>
                  Cancel
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </Modal>
      {/* Stop Modal */}
      <Modal isOpen={stopModal} toggle={toggleStop}>
        <ModalHeader toggle={toggleStop}>Fill the details</ModalHeader>

        <Formik
          initialValues={{
            numberOfBox: null,
            pickerName: [],
            checkPackerName: [],
            mixItem: false,
          }}
          validationSchema={Yup.object().shape({
            numberOfBox: Yup.number().required("Number of Box is required."),
            pickerName: Yup.array()
              .min(1, "Picker name is required.")
              .required("Picker name is required."),
            checkPackerName: Yup.array()
              .min(1, "Check/Packer name is required.")
              .required("Check/Packer name is required."),
          })}
          onSubmit={(values) => {
            handleStopSubmit(pickList.id, {
              status: "STOPPED",
              numberOfBox: values.numberOfBox,
              pickerName: values.pickerName?.map((employee: any) => employee.id),
              checkPackerName: values.checkPackerName?.map((employee: any) => employee.id),
              mixItem: values.mixItem,
            });
          }}
        >
          {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
            <>
              <ModalBody>
                <FormInput
                  label='Number of Box'
                  name='numberOfBox'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={getIn(values, "numberOfBox")}
                  error={getIn(errors, "numberOfBox")}
                  touched={getIn(touched, "numberOfBox")}
                />

                <FormSelect
                  name='pickerName'
                  label='Picker Name'
                  isAsync
                  isMulti
                  getOptions={getEmployees}
                  onChange={(name: any, value: any) => {
                    setFieldValue(name, value);
                  }}
                  onBlur={handleBlur}
                  value={getIn(values, "pickerName")}
                  error={getIn(errors, "pickerName")}
                  touched={getIn(touched, "pickerName")}
                />

                <FormSelect
                  name='checkPackerName'
                  label='Check/Packer Name'
                  isAsync
                  isMulti
                  getOptions={getEmployees}
                  onChange={(name: any, value: any) => {
                    setFieldValue(name, value);
                  }}
                  onBlur={handleBlur}
                  value={getIn(values, "checkPackerName")}
                  error={getIn(errors, "checkPackerName")}
                  touched={getIn(touched, "checkPackerName")}
                />

                <div className='form-check mt-3'>
                  <input
                    type='checkbox'
                    className='form-check-input'
                    id='mixItem'
                    name='mixItem'
                    onChange={handleChange}
                    onBlur={handleBlur}
                    checked={Boolean(values.mixItem)}
                  />
                  <label className='form-check-label' htmlFor='mixItem'>
                    Mix Item
                  </label>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  size='sm'
                  color='primary'
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  Submit
                </Button>{" "}
                <Button size='sm' color='secondary' onClick={toggleStop}>
                  Cancel
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </Modal>
    </div>
  );
}

export default CheckerPanel;
